import { captureError, addBreadcrumb } from '../sentry';

/**
 * Opciones para las peticiones a la API
 */
export interface ApiRequestOptions extends RequestInit {
  /** Tiempo máximo de espera para la petición (ms) */
  timeout?: number;
  /** Indica si la respuesta debe ser cacheada */
  useCache?: boolean;
  /** Tiempo de vida de la caché (ms) */
  cacheTTL?: number;
  /** Path adicional que se añadirá a la URL base */
  path?: string;
  /** Parámetros de query que se añadirán a la URL */
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Respuesta estándar de la API
 */
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  count?: number;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

// Verificación de si estamos en el navegador o en el servidor
const isBrowser = typeof window !== 'undefined';

/**
 * Cliente HTTP para comunicación con API REST
 */
export class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;
  private cacheEnabled: boolean = true;
  private requestCache: Map<string, { data: any; expires: number }> = new Map();
  private sessionRevision: number = 0;
  
  /** TTL por defecto para la caché (5 minutos) */
  private defaultCacheTTL: number = 5 * 60 * 1000;
  
  /** Timeout por defecto (10 segundos) */
  private defaultTimeout: number = 10000;
  
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    
    // Intentar recuperar el token de autenticación del almacenamiento local (solo en el navegador)
    if (isBrowser) {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        this.authToken = storedToken;
      }
    }
  }
  
  /**
   * Establece el token de autenticación para peticiones
   */
  public setAuthToken(token: string | null): void {
    if (this.authToken !== token) {
      this.sessionRevision += 1;
      this.clearCache();
    }
    this.authToken = token;
    
    // Guardar en localStorage solo si estamos en el navegador
    if (isBrowser) {
      if (token) {
        localStorage.setItem('auth_token', token);
      } else {
        localStorage.removeItem('auth_token');
      }
    }
  }
  
  /**
   * Obtiene el token de autenticación actual
   */
  public getAuthToken(): string | null {
    return this.authToken;
  }
  
  /**
   * Habilita la caché de peticiones
   */
  public enableCache(): void {
    this.cacheEnabled = true;
  }
  
  /**
   * Deshabilita la caché de peticiones
   */
  public disableCache(): void {
    this.cacheEnabled = false;
  }
  
  /**
   * Limpia la caché completa
   */
  public clearCache(): void {
    this.requestCache.clear();
  }
  
  /**
   * Limpia una entrada específica de la caché
   */
  public clearCacheEntry(cacheKey: string): void {
    this.requestCache.delete(cacheKey);
  }

  /**
   * Invalida la caché para un endpoint específico
   */
  public invalidateCacheForEndpoint(endpoint: string): void {
    const url = this.buildUrl(endpoint);
    const cacheKey = this.getCacheKey(url);
    this.requestCache.delete(cacheKey);
  }
  
  /**
   * Realiza una petición GET
   */
  public async get<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, undefined, options);
  }
  
  /**
   * Realiza una petición POST
   */
  public async post<T = any>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data, options);
  }
  
  /**
   * Realiza una petición PUT
   */
  public async put<T = any>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data, options);
  }
  
  /**
   * Realiza una petición PATCH
   */
  public async patch<T = any>(
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data, options);
  }
  
  /**
   * Realiza una petición DELETE
   */
  public async delete<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, undefined, options);
  }
  
  /**
   * Método genérico para realizar peticiones HTTP
   */
  private async request<T = any>(
    method: string,
    endpoint: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    // Si estamos en el servidor durante SSR, devolver una respuesta predeterminada
    if (!isBrowser) {
      return {
        success: true,
        data: {} as T,
        message: 'Datos prellenados para SSR'
      };
    }
    
    // Construir la URL completa
    const url = this.buildUrl(endpoint, options.path, options.params);
    const sessionRevision = this.sessionRevision;
    const cacheKey = this.getCacheKey(url);
    
    // Verificar si hay una versión cacheada de la respuesta para peticiones GET
    if (method === 'GET' && this.cacheEnabled && options.useCache !== false) {
      const cachedData = this.requestCache.get(cacheKey);
      
      if (cachedData && cachedData.expires > Date.now()) {
        return cachedData.data;
      }
    }
    
    // Configurar opciones de la petición
    const { timeout = this.defaultTimeout, useCache, cacheTTL, ...fetchOptions } = options;
    
    const headers = new Headers(fetchOptions.headers);
    
    // Añadir token de autenticación si existe
    if (this.authToken) {
      headers.set('Authorization', `Bearer ${this.authToken}`);
    }
    
    // Configurar tipo de contenido si no está ya especificado
    if (data && !headers.has('Content-Type') && !(data instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // Enviar idioma actual para traducciones de productos
    const lang = isBrowser ? (localStorage.getItem('tolgee-language') || 'es') : 'es';
    headers.set('Accept-Language', lang);
    
    // Preparar opciones para fetch con timeout
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    
    try {
      // Siempre incluir credentials: 'include' para enviar/recibir cookies
      const requestOptions: RequestInit = {
        method,
        headers,
        body: data ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined,
        signal: controller.signal,
        credentials: 'include', // Siempre incluir cookies
        ...fetchOptions
      };
      
      const response = await fetch(url, requestOptions);
      
      clearTimeout(id);
      
      // Parsear la respuesta como JSON
      const responseData = await this.parseResponse<T>(response);
      
      // Cachear respuesta para peticiones GET
      if (
        method === 'GET' &&
        this.cacheEnabled &&
        options.useCache !== false &&
        responseData.success &&
        sessionRevision === this.sessionRevision
      ) {
        const ttl = cacheTTL || this.defaultCacheTTL;
        
        this.requestCache.set(cacheKey, {
          data: responseData,
          expires: Date.now() + ttl
        });
      }
      
      return responseData;
    } catch (error) {
      clearTimeout(id);

      if ((error as Error).name === 'AbortError') {
        return {
          success: false,
          error: {
            code: 'TIMEOUT',
            message: `La petición excedió el tiempo de espera (${timeout}ms)`
          }
        };
      }

      // Determinar el mensaje de error más útil para el usuario
      const errorMessage = (error as Error).message || '';
      let userFriendlyMessage = 'Error de conexión con el servidor';
      let errorType = 'unknown';

      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        userFriendlyMessage = 'No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet, desactiva temporalmente bloqueadores de anuncios o extensiones de privacidad, e inténtalo de nuevo.';
        errorType = 'failed_to_fetch';
      } else if (errorMessage.includes('SSL') || errorMessage.includes('certificate')) {
        userFriendlyMessage = 'Error de seguridad en la conexión. Intenta actualizar la página o usa otro navegador.';
        errorType = 'ssl_error';
      } else if (errorMessage.includes('CORS')) {
        userFriendlyMessage = 'Error de acceso. Intenta recargar la página.';
        errorType = 'cors_error';
      }

      // Capturar el error en Sentry con contexto completo
      captureError(error as Error, {
        errorType,
        method,
        url,
        endpoint,
        originalMessage: errorMessage,
        userAgent: navigator.userAgent,
        isOnline: navigator.onLine,
        connectionType: (navigator as any).connection?.effectiveType || 'unknown',
      });

      // Añadir breadcrumb para seguimiento
      addBreadcrumb(`API Error: ${method} ${endpoint}`, 'api', {
        errorType,
        errorMessage,
        isOnline: navigator.onLine,
      });

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: userFriendlyMessage,
          details: errorMessage // Guardar el error original para debugging
        }
      };
    }
  }
  
  /**
   * Construye la URL completa para la petición
   */
  private buildUrl(
    endpoint: string,
    path?: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    // Asegurar que el endpoint comienza con '/'
    if (!endpoint.startsWith('/')) {
      endpoint = '/' + endpoint;
    }
    
    // Añadir path adicional si existe
    if (path) {
      const sanitizedPath = path.startsWith('/') ? path.slice(1) : path;
      endpoint = `${endpoint}/${sanitizedPath}`;
    }
    
    // Construir URL base
    let url = `${this.baseUrl}${endpoint}`;
    
    // Añadir parámetros query si existen
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    
    return url;
  }
  
  /**
   * Genera una clave para la caché basada en la URL
   */
  private getCacheKey(url: string): string {
    const lang = isBrowser ? (localStorage.getItem('tolgee-language') || 'es') : 'es';
    return `api_cache:${this.sessionRevision}:${lang}:${url}`;
  }
  
  /**
   * Mapea _id a id en objetos y arrays recursivamente
   */
  private mapMongoIds(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.mapMongoIds(item));
    }

    if (typeof obj === 'object') {
      const mapped: any = {};

      // Si tiene _id, mapearlo a id
      if (obj._id && !obj.id) {
        mapped.id = obj._id;
      }

      // Copiar todas las propiedades
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          mapped[key] = this.mapMongoIds(obj[key]);
        }
      }

      return mapped;
    }

    return obj;
  }

  /**
   * Parsea la respuesta HTTP según el tipo y código de estado
   */
  private async parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
    try {
      // Intentar parsear como JSON
      const data = await response.json();

      // Si la API devuelve un formato estándar, usarlo directamente
      if ('success' in data) {
        // Mapear _id a id en los datos
        if (data.data) {
          data.data = this.mapMongoIds(data.data);
        }
        return data as ApiResponse<T>;
      }

      // En caso contrario, crear un formato estándar
      if (response.ok) {
        return {
          success: true,
          data: this.mapMongoIds(data) as T
        };
      } else {
        return {
          success: false,
          error: {
            code: String(response.status),
            message: data.message || response.statusText,
            details: data
          }
        };
      }
    } catch (error) {
      // Manejar errores al parsear la respuesta
      if (response.ok) {
        return {
          success: true,
          data: {} as T
        };
      } else {
        return {
          success: false,
          error: {
            code: String(response.status),
            message: response.statusText,
          }
        };
      }
    }
  }
}
